import { isEmptyObject } from '@philips-emr/tws-utils';

import { Value } from '../../types';
import { getAuthenticationConfiguration } from '../configuration-service';
import HistoryService from '../history-service';
import { createURL, HttpHeader, HttpMethod, HttpStatus, MediaType, verifyResponseStatus } from '../http-service';

import JWTToken, { Claims } from './JWTToken';
import PermissionMatcher, { Logical } from './permission-matcher';

export const ACCESS_TOKEN = 'access_token';
export const REFRESH_TOKEN = 'refresh_token';
export const TOKEN_TYPE = 'token_type';

interface Endpoint {
  method: HttpMethod;
  url: string;
}

export enum OAuthGrantType {
  PASSWORD = 'password'
}

export type OAuthForm = {
  client_id: string;
  grant_type?: OAuthGrantType;
  password?: string;
  refresh_token?: string;
  username?: string;
};

export interface AuthenticationConfiguration {
  clientId: string;
  endpoints: {
    information: Endpoint;
    refresh: Endpoint;
    revoke: Endpoint;
  };
  endpointMappers: {
    information: <T>(data: object) => T;
  };
  inactivity: {
    time: number;
  };
  routes: {
    custom?: string[];
    forgotPassword: string;
    home: string;
    inactivity: string;
    login: string;
  };
}

interface LoginParameters {
  username: string;
  password: string;
  [key: string]: Value;
}

export interface OAuthResponse {
  access_token: string;
  expires_in: number;
  refresh_expires: number;
  refresh_token: string;
  token_type: string;
}

interface StorageData {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
}

function getConfiguration(): AuthenticationConfiguration {
  const configuration = getAuthenticationConfiguration();

  if (!configuration || isEmptyObject(configuration)) {
    throw Error('[Authentication Service]: Service is not configured.');
  }

  return configuration;
}

function retrieveStorageData(): StorageData {
  return {
    accessToken: localStorage.getItem(ACCESS_TOKEN) || '',
    refreshToken: localStorage.getItem(REFRESH_TOKEN) || '',
    tokenType: localStorage.getItem(TOKEN_TYPE) || '',
  };
}

function formatRequestBody(parameters: Record<string, string>): string {
  const parametersReducer = (accumulator: URLSearchParams, key: string): URLSearchParams => {
    accumulator.append(key, parameters[key]);
    return accumulator;
  };
  return Object.keys(parameters).reduce(parametersReducer, new URLSearchParams()).toString();
}

function request(endpoint: Endpoint, parameters: Record<string, string>, headers: Headers = new Headers()): Promise<Response> {
  const { method = 'POST', url } = endpoint;

  const body = formatRequestBody(parameters);
  
  headers.append(HttpHeader.ACCEPT, MediaType.APPLICATION_JSON);
  headers.append(HttpHeader.CONTENT_TYPE, MediaType.APPLICATION_FORM_URLENCODED);
    
  return fetch(createURL(url).toString(), {
    body,
    method,
    headers,
  });
}

export interface AuthenticationService {
  getJWTToken(): JWTToken | null;
  getRefreshToken(): string;
  hasLogoutListeners(): boolean;
  isAuthenticated(): boolean;
  listenLogout(listener: Function): () => boolean;
  login(parameters: LoginParameters): Promise<JWTToken>;
  logout(): Promise<Response>;
  matchClaims(claims: Claims, logical: Logical): boolean;
  matchPermissions(permissions: string[], logical: Logical): boolean;
  redirectToForgotPassword(): void;
  redirectToHome(): void;
  redirectToInactivity(): void;
  redirectToLogin(): void;
  requestRefreshToken(): Promise<JWTToken>;
  updateJWTToken(response: OAuthResponse): Promise<JWTToken>;
}

function createAuthenticationService(): AuthenticationService {
  
  const logoutListeners = new Set<Function>();
  let jwtToken: JWTToken | null = null;
  let matcher: PermissionMatcher | null = null;
  let pendingRefresh: Promise<JWTToken> | null = null;

  const { tokenType, accessToken } = retrieveStorageData();

  if (tokenType && accessToken) {
    const token = new JWTToken(tokenType, accessToken);

    jwtToken = token;
    matcher = new PermissionMatcher(token);
  }

  const clearTokens = (): void => {
    jwtToken = null;
    matcher = null;

    localStorage.removeItem(ACCESS_TOKEN);
    localStorage.removeItem(REFRESH_TOKEN);

    logoutListeners.forEach((listener) => listener());
  };

  const refreshToken = (parameters: {}): Promise<JWTToken> => {
    if (pendingRefresh) {
      return pendingRefresh;
    }

    const { clientId, endpoints } = getConfiguration();

    const requestParameters = {
      'client_id': clientId,
      ...parameters,
    };

    const refreshPromise = request(endpoints.refresh, requestParameters)
      .then((response: Response) => {
        pendingRefresh = null;

        return verifyResponse(response);
      })
      .then((response: Response) => response.json())
      .then((response: OAuthResponse) => storeToken(response))
      .catch(e => {
        pendingRefresh = null;
        throw e;
      });

    pendingRefresh = refreshPromise;

    return refreshPromise;
  };

  const revokeToken = (): Promise<Response> => {
    const { clientId, endpoints } = getConfiguration();
    const { refreshToken } = retrieveStorageData();

    const parameters = {
      'client_id': clientId,
      'refresh_token': refreshToken,
    };

    const token = getJWTToken();
    const headers = new Headers();
    
    if (token) {
      headers.append(HttpHeader.AUTHORIZATION, `${token.getType()} ${token.rawToken()}`);
    }

    return request(endpoints.revoke, parameters, headers);
  };

  const storeToken = (response: OAuthResponse): Promise<JWTToken> => {
    const token = new JWTToken(response.token_type, response.access_token);

    jwtToken = token;
    matcher = new PermissionMatcher(token);

    localStorage.setItem(ACCESS_TOKEN, response.access_token);
    localStorage.setItem(REFRESH_TOKEN, response.refresh_token);
    localStorage.setItem(TOKEN_TYPE, response.token_type);

    return Promise.resolve(token);
  };

  const verifyResponse = (response: Response): Response => {
    if (response.status === HttpStatus.UNAUTHORIZED) {
      clearTokens();
      redirectToLogin();
    }

    return verifyResponseStatus(response);
  };

  function getJWTToken(): JWTToken | null {
    return jwtToken;
  }

  function getRefreshToken(): string {
    const { refreshToken } = retrieveStorageData();
    return refreshToken;
  }

  function isAuthenticated(): boolean {
    return jwtToken !== null;
  }

  function listenLogout(listener: Function): () => boolean {
    if (logoutListeners.has(listener)) {
      throw Error('[Authentication Service]: Listener already added.');
    }

    logoutListeners.add(listener);
    return () => logoutListeners.delete(listener);
  }

  function hasLogoutListeners(): boolean {
    return logoutListeners.size > 0;
  }

  function login(parameters: LoginParameters): Promise<JWTToken> {
    if (isAuthenticated()) {
      throw Error('[Authentication Service]: Already authenticated.');
    }

    return refreshToken({ 'grant_type': OAuthGrantType.PASSWORD, ...parameters });
  }

  function logout(): Promise<Response> {
    try {
      return revokeToken();
    } finally {
      clearTokens();
    }
  }

  function matchClaims(claims: Claims, logical = Logical.AND): boolean {
    if (!isAuthenticated()) {
      return false;
    }

    return matcher ? matcher.matchClaims(claims, logical) : false;
  }

  function matchPermissions(permissions: string[], logical = Logical.AND): boolean {
    if (!isAuthenticated()) {
      return false;
    }

    return matcher ? matcher.matchPermissions(permissions, logical) : false;
  }

  function redirectToForgotPassword(): void {
    const configuration = getConfiguration();
    HistoryService.push(configuration.routes.forgotPassword);
  }

  function redirectToHome(): void {
    const configuration = getConfiguration();
    HistoryService.push(configuration.routes.home);
  }

  function redirectToInactivity(): void {
    const configuration = getConfiguration();
    HistoryService.push(configuration.routes.inactivity);
  }

  function redirectToLogin(): void {
    const configuration = getConfiguration();
    HistoryService.push(configuration.routes.login);
  }

  function requestRefreshToken(): Promise<JWTToken> {
    const parameters = {
      'grant_type': REFRESH_TOKEN,
      'refresh_token': retrieveStorageData().refreshToken,
    };

    return refreshToken(parameters);
  }

  function updateJWTToken(response: OAuthResponse): Promise<JWTToken> {
    return storeToken(response);
  }

  return {
    getJWTToken,
    getRefreshToken,
    hasLogoutListeners,
    isAuthenticated,
    listenLogout,
    login,
    logout,
    matchClaims,
    matchPermissions,
    redirectToForgotPassword,
    redirectToHome,
    redirectToInactivity,
    redirectToLogin,
    requestRefreshToken,
    updateJWTToken,
  };
}

const authenticationService = createAuthenticationService();

export default authenticationService;
